// The Hiring Request's Journey (lane B2 S08): his three steps — "HRBP request creation, HM certification, HRBP
// approval" (G §4 step 1) — from the request's own events; a done step carries its actor · date, the step that returned
// a request is marked Returned. A request that reached the HR Business Partner (submitted → certified → pending HRBP)
// has passed its certification, as V1's request the Hiring Manager raised and submitted did, and step 1 is named for its
// actor (F-51). Pure, so tests/mc/b2-request.test.mjs reads it under SSR.

import type { RequestOpenOutput } from "@/api/tools";
import { formatDate, JOURNEY_LABELS, REQUEST_STATUS, ROLE_LABELS, type JourneyStep } from "@/mc";
import { STATUS_WORD } from "./data";

export const DRAFT_STATES = new Set(["drafted", "returned", "dormant"]);

/** The statuses past certification: the request waits on, or was decided by, the HR Business Partner. */
const PAST_CERTIFICATION = new Set(["submitted", "question_raised", "approved", "converted"]);

/** Step 1 named for the person who created the request: his *HRBP request creation*, or, for a request its Hiring
 * Manager raised (V1's "HM-originated request creation", where the originator is the Hiring Manager), the same words
 * with *HM*. */
export function creationLabel(r: Pick<RequestOpenOutput, "requester_id" | "intended_hm_id"> | null): string {
  const hmRaised = r !== null && !!r.requester_id && (r.intended_hm_id ?? r.requester_id) === r.requester_id;
  return hmRaised ? JOURNEY_LABELS.hrbpRequestCreation.replace(ROLE_LABELS.hrbpAbbr, ROLE_LABELS.hmAbbr) : JOURNEY_LABELS.hrbpRequestCreation;
}

function lastEvent(r: RequestOpenOutput, kinds: readonly string[]) {
  return [...r.events].reverse().find((event) => kinds.includes(event.kind));
}

function stamp(r: RequestOpenOutput, kinds: readonly string[]): string | undefined {
  const event = lastEvent(r, kinds);
  return event ? `${event.by_name ?? ""} · ${formatDate(event.at)}` : undefined;
}

export function journeySteps(r: RequestOpenOutput | null): JourneyStep[] {
  if (!r) {
    return [
      { label: creationLabel(null), state: "active" },
      { label: JOURNEY_LABELS.hmCertification, state: "next" },
      { label: JOURNEY_LABELS.hrbpApproval, state: "next" },
    ];
  }
  const journey = (r.journey ?? []) as { step: string; done: boolean }[];
  const done = (step: string) => journey.find((entry) => entry.step === step)?.done ?? false;
  const returned = r.status === "returned" || r.status === "dormant";
  // submitted → certified → pending HRBP: a request that reached the HR Business Partner was certified on the way
  const certified = done("hm_certified") || done("pending_hrbp_approval") || PAST_CERTIFICATION.has(r.status);
  const approved = r.status === "approved" || r.status === "converted";
  const routed = done("pending_hm_cert");
  const create: JourneyStep = {
    label: creationLabel(r),
    state: DRAFT_STATES.has(r.status) ? "active" : "done",
    sub: DRAFT_STATES.has(r.status) ? STATUS_WORD[r.status] : stamp(r, ["routed_to_hm", "created"]),
  };
  const certify: JourneyStep = {
    label: JOURNEY_LABELS.hmCertification,
    state: returned && !certified ? "returned" : certified || approved ? "done" : routed && r.status === "pending_hm_cert" ? "active" : "next",
    sub: certified ? stamp(r, ["hm_certified"]) ?? stamp(r, ["submitted", "resubmitted"]) : returned && !certified ? REQUEST_STATUS.returned : undefined,
  };
  const approve: JourneyStep = {
    label: JOURNEY_LABELS.hrbpApproval,
    state: approved ? "done" : returned && certified ? "returned" : certified ? "active" : "next",
    sub: approved ? stamp(r, ["approved"]) : returned && certified ? REQUEST_STATUS.returned : undefined,
  };
  return [create, certify, approve];
}

